import { SchmancyElement, schmancyElement } from '@mhmo91/schmancy/mixins'
import '@mhmo91/schmancy/audio'
import '@mhmo91/schmancy/divider'
import '@mhmo91/schmancy/form/fields/chips'
import '@mhmo91/schmancy/form/fields/range'
import '@mhmo91/schmancy/icons'
import '@mhmo91/schmancy/button'
import '@mhmo91/schmancy/typography'
import { css, html } from 'lit'
import { property } from 'lit/decorators.js'
import { repeat } from 'lit/directives/repeat.js'
import { when } from 'lit/directives/when.js'
import { DEFAULT_ACCENTS, DEFAULT_SCHEME_LABELS, type ThemeAccent, type ThemeSchemeLabels } from '../theme-config'

export interface SettingsLanguage {
	value: string
	label: string
}

/** Device preferences; app-owned account controls belong in the default slot. */
@schmancyElement('schmancy-settings')
export class SchmancySettings extends SchmancyElement {
	static styles = [css`:host { display: block; }`]

	@property({ attribute: false }) colors: ThemeAccent[] = DEFAULT_ACCENTS
	@property() themeLabel = 'Theme'
	@property() closeLabel = 'Close'
	@property({ attribute: false }) schemeLabels: ThemeSchemeLabels = DEFAULT_SCHEME_LABELS
	@property() radiusLabel = 'Corner rounding (%)'
	@property() soundLabel = 'Sound'
	@property() volumeLabel = 'Volume'
	@property() languageLabel = 'Language'
	@property({ attribute: false }) languages: SettingsLanguage[] = []
	@property() language = ''

	override render() {
		return html`
			<div class="flex flex-col gap-4 p-4">
				<div class="flex items-center gap-3">
					<schmancy-icon class="text-surface-onVariant text-base">palette</schmancy-icon>
					<schmancy-typography type="body" token="sm" class="text-surface-onVariant flex-1">${this.themeLabel}</schmancy-typography>
					<schmancy-icon-button
						size="sm"
						variant="text"
						.icon=${'close'}
						aria-label=${this.closeLabel}
						title=${this.closeLabel}
						@click=${() => this.dispatchEvent(new CustomEvent('close', { bubbles: true, composed: true }))}
					></schmancy-icon-button>
				</div>
				<schmancy-theme-config
					.label=${''}
					.colors=${this.colors}
					.schemeLabels=${this.schemeLabels}
					.radiusLabel=${this.radiusLabel}
				></schmancy-theme-config>
				<schmancy-divider></schmancy-divider>
				<schmancy-sound-control .label=${this.soundLabel} .volumeLabel=${this.volumeLabel}></schmancy-sound-control>
				${when(this.languages.length > 0, () => html`
					<schmancy-divider></schmancy-divider>
					<div class="flex flex-col gap-3">
						<schmancy-typography type="body" token="sm">${this.languageLabel}</schmancy-typography>
						<schmancy-chips
							class="w-full"
							controlled
							required
							.label=${this.languageLabel}
							.value=${this.language}
							@change=${(event: CustomEvent<{ value: string | string[] }>) => {
								const value = event.detail.value
								if (typeof value !== 'string' || value === this.language || !this.languages.some(option => option.value === value)) return
								this.dispatchEvent(new CustomEvent('language-change', { detail: { value }, bubbles: true, composed: true }))
							}}
						>
							${repeat(this.languages, option => option.value, option => html`
								<schmancy-filter-chip size="xs" .value=${option.value}>${option.label}</schmancy-filter-chip>
							`)}
						</schmancy-chips>
					</div>
				`)}
				<slot></slot>
			</div>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-settings': SchmancySettings
	}
}
